/**
 * NOM-035 Employee Panel
 * Vista simplificada para empleados con acceso a:
 * - Buzón Anónimo (enviar y consultar mensajes)
 * - Cuestionarios de Guía I y III
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Mail,
  Send,
  Search,
  Shield,
  CheckCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Copy,
  ArrowLeft,
  Check,
  FileText,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { setQuestionnaireMode } from '../utils/questionnaireMode';
import logger from '../utils/logger';

const NOM035EmployeePanel = ({ user }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [nom035Activo, setNom035Activo] = useState(false);
  const [showBuzonDialog, setShowBuzonDialog] = useState(false);
  const [showGuia1Dialog, setShowGuia1Dialog] = useState(false);
  const [showGuia3Dialog, setShowGuia3Dialog] = useState(false);
  const [cuestionariosPendientes, setCuestionariosPendientes] = useState([]);

  useEffect(() => {
    if (user?.empresa_id) {
      checkNom035Status();
    }
  }, [user]);

  const checkNom035Status = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${user.empresa_id}/employee-status`, {
        params: { user_id: user.id }
      });
      if (res.data.success) {
        setNom035Activo(res.data.data.activo);
        setCuestionariosPendientes(res.data.data.cuestionarios_pendientes || []);
      }
    } catch (error) {
      // Si no hay config, el módulo no está activo
      setNom035Activo(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (!nom035Activo) {
    return null; // No mostrar nada si NOM-035 no está activo
  }

  return (
    <>
      <Card className="mb-6 border-l-4 border-l-purple-500">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-purple-600" />
            NOM-035 - {language === 'es' ? 'Bienestar Laboral' : 'Workplace Wellness'}
          </CardTitle>
          <CardDescription>
            {language === 'es' 
              ? 'Canal de comunicación y evaluación de factores de riesgo psicosocial'
              : 'Communication channel and psychosocial risk factor assessment'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Buzón Anónimo */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-purple-900">
                  {language === 'es' ? 'Buzón Anónimo' : 'Anonymous Mailbox'}
                </h3>
              </div>
              <p className="text-sm text-purple-700 mb-3">
                {language === 'es' 
                  ? 'Reporta situaciones de riesgo de forma confidencial. Tu identidad está protegida.'
                  : 'Report risk situations confidentially. Your identity is protected.'}
              </p>
              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={() => setShowBuzonDialog(true)}
                data-testid="employee-buzon-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Acceder al buzón' : 'Access mailbox'}
              </Button>
            </div>

            {/* Cuestionarios Pendientes */}
            <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-5 h-5 text-teal-600" />
                <h3 className="font-semibold text-teal-900">
                  {language === 'es' ? 'Cuestionarios NOM-035' : 'NOM-035 Questionnaires'}
                </h3>
              </div>
              {cuestionariosPendientes.length > 0 ? (
                <div className="space-y-3">
                  {cuestionariosPendientes.map((c, idx) => (
                    <div key={idx} className="p-3 bg-white rounded-lg border shadow-sm">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-teal-600 mt-1" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {language === 'es' ? c.nombre_es : c.nombre_en}
                            </p>
                            <p className="text-xs text-gray-500">
                              {language === 'es' ? c.descripcion_es : c.descripcion_en}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                          {language === 'es' ? 'Pendiente' : 'Pending'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">
                          ⏱ {c.tiempo_estimado}
                        </span>
                        <Button 
                          size="sm" 
                          className="bg-teal-600 hover:bg-teal-700"
                          onClick={() => {
                            // Navigate to the appropriate questionnaire
                            if (c.id === 'GUIA_I') {
                              setShowGuia1Dialog(true);
                            } else if (c.id === 'GUIA_II_III') {
                              // Navigate to full-page questionnaire
                              navigate('/nom035/guide3');
                            }
                          }}
                          data-testid={`start-${c.id}-btn`}
                        >
                          {language === 'es' ? 'Iniciar' : 'Start'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-teal-700">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">
                    {language === 'es' ? 'No tienes cuestionarios pendientes' : 'No pending questionnaires'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diálogo del Buzón Anónimo */}
      <BuzonAnonimoDialog 
        open={showBuzonDialog}
        onOpenChange={setShowBuzonDialog}
        empresaId={user.empresa_id}
        userId={user.id}
      />

      {/* Diálogo Guía I - ATS */}
      <Guia1EmployeeDialog
        open={showGuia1Dialog}
        onOpenChange={setShowGuia1Dialog}
        empresaId={user.empresa_id}
        userId={user.id}
        onComplete={() => {
          setShowGuia1Dialog(false);
          checkNom035Status(); // Refresh pending questionnaires
        }}
      />

      {/* Diálogo Guía II/III */}
      <Guia3EmployeeDialog
        open={showGuia3Dialog}
        onOpenChange={setShowGuia3Dialog}
        empresaId={user.empresa_id}
        userId={user.id}
        onComplete={() => {
          setShowGuia3Dialog(false);
          checkNom035Status(); // Refresh pending questionnaires
        }}
      />
    </>
  );
};


// Componente del Buzón Anónimo para empleados - M8-E-REV v2
const BuzonAnonimoDialog = ({ open, onOpenChange, empresaId, userId }) => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('enviar');
  const [step, setStep] = useState('form');
  const [categorias, setCategorias] = useState([]);
  const [categoriasCanalizacion, setCategoriasCanalizacion] = useState([]);
  const [responsableNombre, setResponsableNombre] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [categoria, setCategoria] = useState('');
  const [esAnonimo, setEsAnonimo] = useState(false);
  const [aceptaConsentimiento, setAceptaConsentimiento] = useState(false);
  const [codigoSeguimiento, setCodigoSeguimiento] = useState('');
  const [mensajeEsAnonimo, setMensajeEsAnonimo] = useState(false);
  const [codigoConsulta, setCodigoConsulta] = useState('');
  const [resultadoConsulta, setResultadoConsulta] = useState(null);
  const [consulting, setConsulting] = useState(false);
  const [misMensajes, setMisMensajes] = useState([]);
  const [loadingMensajes, setLoadingMensajes] = useState(false);

  const categoriaRequiereCanalizacion = categoriasCanalizacion.includes(categoria);

  useEffect(() => {
    if (open) {
      loadConsentimiento();
      setStep('form');
      setMensaje('');
      setCategoria('');
      setEsAnonimo(false);
      setAceptaConsentimiento(false);
      setCodigoSeguimiento('');
      setCodigoConsulta('');
      setResultadoConsulta(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && activeTab === 'mis-mensajes') {
      loadMisMensajes();
    }
  }, [activeTab, open]);

  const loadConsentimiento = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/buzon/consentimiento?lang=${language}`);
      if (res.data.success) {
        setCategorias(res.data.data.categorias || []);
        setCategoriasCanalizacion(res.data.data.categorias_canalizacion_obligatoria || []);
        setResponsableNombre(res.data.data.responsable_nombre || '');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error cargando información' : 'Error loading information');
    } finally {
      setLoading(false);
    }
  };

  const loadMisMensajes = async () => {
    if (!userId) return;
    setLoadingMensajes(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/buzon/mis-mensajes/${userId}?lang=${language}`);
      if (res.data.success) {
        setMisMensajes(res.data.data.mensajes || []);
      }
    } catch (error) {
      logger.error('Error loading mis mensajes:', error);
    } finally {
      setLoadingMensajes(false);
    }
  };

  const handleEnviar = async () => {
    if (!categoria) {
      toast.error(language === 'es' ? 'Selecciona una categoría' : 'Select a category');
      return;
    }
    if (!mensaje.trim() || mensaje.trim().length < 10) {
      toast.error(language === 'es' ? 'El mensaje debe tener al menos 10 caracteres' : 'Message must have at least 10 characters');
      return;
    }
    if (!aceptaConsentimiento) {
      toast.error(language === 'es' ? 'Debes aceptar los términos' : 'You must accept the terms');
      return;
    }
    
    const seraAnonimo = esAnonimo && !categoriaRequiereCanalizacion;
    
    setSending(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/buzon/enviar?user_id=${userId || ''}`, {
        contenido: mensaje,
        categoria: categoria,
        es_anonimo: seraAnonimo,
        acepta_consentimiento: true
      });
      
      if (res.data.success) {
        setCodigoSeguimiento(res.data.data.codigo_seguimiento);
        setMensajeEsAnonimo(res.data.data.es_anonimo);
        setStep('confirmacion');
        toast.success(res.data.data.es_anonimo 
          ? (language === 'es' ? 'Mensaje enviado de forma anónima' : 'Message sent anonymously')
          : (language === 'es' ? 'Mensaje enviado de forma confidencial' : 'Message sent confidentially')
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al enviar' : 'Error sending'));
    } finally {
      setSending(false);
    }
  };

  const handleConsultar = async () => {
    if (!codigoConsulta.trim() || codigoConsulta.length !== 6) {
      toast.error(language === 'es' ? 'Ingresa un código de 6 dígitos' : 'Enter a 6-digit code');
      return;
    }
    
    setConsulting(true);
    setResultadoConsulta(null);
    try {
      const res = await axios.get(`/v5/nom035/buzon/consultar/${codigoConsulta.toUpperCase()}?lang=${language}`);
      if (res.data.success) {
        setResultadoConsulta(res.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Código no encontrado' : 'Code not found'));
    } finally {
      setConsulting(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(codigoSeguimiento);
    toast.success(language === 'es' ? 'Código copiado' : 'Code copied');
  };

  const estadoColors = {
    NUEVO: 'bg-blue-100 text-blue-800',
    EN_REVISION: 'bg-yellow-100 text-yellow-800',
    ATENDIDO: 'bg-green-100 text-green-800'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-600" />
            {language === 'es' ? 'Buzón de Comunicación' : 'Communication Mailbox'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es' 
              ? 'Canal seguro para reportar situaciones de riesgo psicosocial'
              : 'Safe channel to report psychosocial risk situations'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Tab navigation */}
            <div className="flex border-b">
              <button
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'enviar' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('enviar')}
              >
                <Send className="w-4 h-4 inline mr-2" />
                {language === 'es' ? 'Enviar' : 'Send'}
              </button>
              <button
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'mis-mensajes' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('mis-mensajes')}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                {language === 'es' ? 'Mis Mensajes' : 'My Messages'}
              </button>
              <button
                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'consulta' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('consulta')}
              >
                <Search className="w-4 h-4 inline mr-2" />
                {language === 'es' ? 'Consultar' : 'Check'}
              </button>
            </div>

            {/* TAB: ENVIAR */}
            {activeTab === 'enviar' && (
              step === 'form' ? (
                <div className="space-y-4">
                  {responsableNombre && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-purple-700">
                        <span className="font-medium">{language === 'es' ? 'Responsable NOM-035: ' : 'NOM-035 Responsible: '}</span>
                        {responsableNombre}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Categoría del reporte *' : 'Report category *'}</Label>
                    <Select value={categoria} onValueChange={(v) => { setCategoria(v); setEsAnonimo(false); }}>
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'es' ? 'Selecciona una categoría' : 'Select a category'} />
                      </SelectTrigger>
                      <SelectContent>
                        {categorias.map(cat => (
                          <SelectItem key={cat.key} value={cat.key}>
                            {cat.nombre}
                            {cat.requiere_canalizacion && ' ⚠️'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {categoria && (
                    <div className={`p-3 rounded-lg border ${categoriaRequiereCanalizacion ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'}`}>
                      {categoriaRequiereCanalizacion ? (
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-amber-800">
                            <p className="font-medium">{language === 'es' ? 'Canalización obligatoria' : 'Mandatory channeling'}</p>
                            <p className="mt-1">
                              {language === 'es' 
                                ? 'Por la naturaleza de este reporte, tu identidad será compartida únicamente con el Responsable NOM-035 para dar seguimiento adecuado.'
                                : 'Due to the nature of this report, your identity will be shared only with the NOM-035 Responsible for proper follow-up.'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-blue-800">
                            {language === 'es' 
                              ? 'Puedes elegir si deseas enviar este mensaje de forma anónima o confidencial.'
                              : 'You can choose whether to send this message anonymously or confidentially.'}
                          </p>
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={esAnonimo}
                              onChange={(e) => setEsAnonimo(e.target.checked)}
                              className="w-4 h-4 text-purple-600 rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700">
                              {language === 'es' 
                                ? 'Enviar de forma anónima (tu identidad NO será visible)'
                                : 'Send anonymously (your identity will NOT be visible)'}
                            </span>
                          </label>
                          <p className="text-xs text-gray-500">
                            {esAnonimo
                              ? (language === 'es' ? 'Mensaje ANÓNIMO: Nadie podrá identificarte.' : 'ANONYMOUS message: No one will identify you.')
                              : (language === 'es' ? 'Mensaje CONFIDENCIAL: Solo el Responsable verá tu identidad.' : 'CONFIDENTIAL message: Only the Responsible will see your identity.')
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Describe la situación *' : 'Describe the situation *'}</Label>
                    <Textarea
                      value={mensaje}
                      onChange={(e) => setMensaje(e.target.value)}
                      placeholder={language === 'es' ? 'Describe la situación con detalle...' : 'Describe the situation in detail...'}
                      className="min-h-[120px]"
                      maxLength={1500}
                    />
                    <p className="text-xs text-gray-500 text-right">{mensaje.length}/1500</p>
                  </div>

                  <div className="p-3 bg-gray-50 rounded-lg border">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={aceptaConsentimiento}
                        onChange={(e) => setAceptaConsentimiento(e.target.checked)}
                        className="mt-1 w-4 h-4 text-purple-600 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">
                        {language === 'es'
                          ? 'Confirmo que la información es verídica y acepto que sea tratada conforme a la política de prevención.'
                          : 'I confirm the information is true and accept it will be treated according to the prevention policy.'}
                      </span>
                    </label>
                  </div>

                  <Button 
                    onClick={handleEnviar} 
                    disabled={sending || !categoria || !mensaje.trim() || !aceptaConsentimiento}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    {language === 'es' ? 'Enviar mensaje' : 'Send message'}
                  </Button>
                </div>
              ) : (
                <div className="text-center space-y-4 py-4">
                  <div className={`w-16 h-16 rounded-full ${mensajeEsAnonimo ? 'bg-purple-100' : 'bg-green-100'} flex items-center justify-center mx-auto`}>
                    {mensajeEsAnonimo ? <Shield className="w-8 h-8 text-purple-600" /> : <CheckCircle className="w-8 h-8 text-green-600" />}
                  </div>
                  <h3 className="text-lg font-semibold">{language === 'es' ? '¡Mensaje enviado!' : 'Message sent!'}</h3>
                  <Badge className={mensajeEsAnonimo ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}>
                    {mensajeEsAnonimo ? (language === 'es' ? 'Anónimo' : 'Anonymous') : (language === 'es' ? 'Confidencial' : 'Confidential')}
                  </Badge>
                  
                  <div className="p-4 bg-gray-100 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">{language === 'es' ? 'Tu código de seguimiento:' : 'Your tracking code:'}</p>
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-2xl font-mono font-bold tracking-wider">{codigoSeguimiento}</span>
                      <Button variant="ghost" size="sm" onClick={copyCode}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600">
                    {mensajeEsAnonimo
                      ? (language === 'es' ? 'Guarda este código para consultar el estado.' : 'Save this code to check the status.')
                      : (language === 'es' ? 'También puedes ver el estado en "Mis Mensajes".' : 'You can also check in "My Messages".')
                    }
                  </p>

                  <Button variant="outline" onClick={() => { setStep('form'); setMensaje(''); setCategoria(''); setAceptaConsentimiento(false); setEsAnonimo(false); }}>
                    {language === 'es' ? 'Enviar otro mensaje' : 'Send another message'}
                  </Button>
                </div>
              )
            )}

            {/* TAB: MIS MENSAJES */}
            {activeTab === 'mis-mensajes' && (
              loadingMensajes ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
                </div>
              ) : misMensajes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{language === 'es' ? 'No tienes mensajes confidenciales enviados' : 'You have no confidential messages sent'}</p>
                  <p className="text-sm mt-1">
                    {language === 'es' 
                      ? 'Los mensajes anónimos solo pueden consultarse con código'
                      : 'Anonymous messages can only be checked with code'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {misMensajes.map(msg => (
                    <div key={msg.id} className="p-4 bg-white rounded-lg border shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{msg.categoria_nombre}</Badge>
                        <Badge className={estadoColors[msg.estado]}>{msg.estado_display}</Badge>
                      </div>
                      <p className="text-sm text-gray-700">{msg.fragmento}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(msg.fecha).toLocaleDateString()} • {language === 'es' ? 'Código:' : 'Code:'} {msg.codigo_seguimiento}
                      </p>
                      {msg.tiene_respuesta && (
                        <div className="mt-3 p-3 bg-purple-50 rounded border border-purple-200">
                          <p className="text-xs text-purple-600 font-medium mb-1">{language === 'es' ? 'Respuesta:' : 'Response:'}</p>
                          <p className="text-sm text-gray-700">{msg.respuesta}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            )}

            {/* TAB: CONSULTA */}
            {activeTab === 'consulta' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Código de seguimiento' : 'Tracking code'}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={codigoConsulta}
                      onChange={(e) => setCodigoConsulta(e.target.value.toUpperCase())}
                      placeholder="ABC123"
                      maxLength={6}
                      className="font-mono text-lg tracking-wider"
                    />
                    <Button onClick={handleConsultar} disabled={consulting}>
                      {consulting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {resultadoConsulta && (
                  <div className="p-4 bg-gray-50 rounded-lg border space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{language === 'es' ? 'Estado:' : 'Status:'}</span>
                      <Badge className={estadoColors[resultadoConsulta.estado]}>{resultadoConsulta.estado_display}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{language === 'es' ? 'Categoría:' : 'Category:'}</span>
                      <span className="text-sm">{resultadoConsulta.categoria_display || '-'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{language === 'es' ? 'Fecha:' : 'Date:'}</span>
                      <span className="text-sm">{new Date(resultadoConsulta.fecha_envio).toLocaleDateString()}</span>
                    </div>
                    
                    {resultadoConsulta.respuesta_publica && (
                      <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                        <p className="text-xs text-purple-600 font-medium mb-1">{language === 'es' ? 'Respuesta:' : 'Response:'}</p>
                        <p className="text-sm">{resultadoConsulta.respuesta_publica}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Componente para Guía I - Identificación de ATS
const Guia1EmployeeDialog = ({ open, onOpenChange, empresaId, userId, onComplete }) => {
  const { language } = useLanguage();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  
  const questions = [
    {
      id: 1,
      text_es: '¿Ha presenciado o sufrido alguna vez, durante o con motivo del trabajo un acontecimiento que le haya causado mucho miedo o que le provocara un estado de shock?',
      text_en: 'Have you ever witnessed or suffered, during or because of work, an event that caused you great fear or shock?'
    },
    {
      id: 2,
      text_es: '¿Ha sido amenazado o agredido físicamente durante su trabajo?',
      text_en: 'Have you been threatened or physically assaulted during your work?'
    },
    {
      id: 3,
      text_es: '¿Ha sufrido algún accidente grave de trabajo?',
      text_en: 'Have you suffered a serious work accident?'
    },
    {
      id: 4,
      text_es: '¿Ha visto morir o ha muerto algún compañero durante el trabajo?',
      text_en: 'Have you seen a coworker die during work?'
    }
  ];
  
  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const tieneAts = Object.values(answers).some(a => a === 'SI');
      
      await axios.post(`/v5/nom035/${empresaId}/guide1/employee-response`, {
        user_id: userId,
        respuestas: answers,
        tiene_ats: tieneAts
      });
      
      setCompleted(true);
      toast.success(language === 'es' ? 'Cuestionario completado' : 'Questionnaire completed');
      
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
    } finally {
      setSubmitting(false);
    }
  };
  
  const allAnswered = questions.every(q => answers[q.id]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-teal-600" />
            {language === 'es' ? 'Guía I - Identificación de ATS' : 'Guide I - STE Identification'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es' 
              ? 'Responde las siguientes preguntas sobre acontecimientos traumáticos severos.'
              : 'Answer the following questions about severe traumatic events.'}
          </DialogDescription>
        </DialogHeader>
        
        {completed ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-semibold text-green-700">
              {language === 'es' ? '¡Gracias por completar el cuestionario!' : 'Thank you for completing the questionnaire!'}
            </h3>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="text-sm text-gray-500 mb-4">
              {language === 'es' ? 'Pregunta' : 'Question'} {currentQuestion + 1} / {questions.length}
            </div>
            
            {questions.map((q, idx) => (
              <div key={q.id} className={idx === currentQuestion ? 'block' : 'hidden'}>
                <p className="text-sm font-medium mb-4">{language === 'es' ? q.text_es : q.text_en}</p>
                <div className="flex gap-4">
                  <Button
                    variant={answers[q.id] === 'SI' ? 'default' : 'outline'}
                    className={answers[q.id] === 'SI' ? 'bg-red-600 hover:bg-red-700' : ''}
                    onClick={() => handleAnswer(q.id, 'SI')}
                  >
                    {language === 'es' ? 'Sí' : 'Yes'}
                  </Button>
                  <Button
                    variant={answers[q.id] === 'NO' ? 'default' : 'outline'}
                    className={answers[q.id] === 'NO' ? 'bg-green-600 hover:bg-green-700' : ''}
                    onClick={() => handleAnswer(q.id, 'NO')}
                  >
                    No
                  </Button>
                </div>
              </div>
            ))}
            
            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                disabled={currentQuestion === 0}
              >
                {language === 'es' ? 'Anterior' : 'Previous'}
              </Button>
              
              {currentQuestion < questions.length - 1 ? (
                <Button
                  onClick={() => setCurrentQuestion(prev => prev + 1)}
                  disabled={!answers[questions[currentQuestion].id]}
                >
                  {language === 'es' ? 'Siguiente' : 'Next'}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!allAnswered || submitting}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {submitting 
                    ? (language === 'es' ? 'Enviando...' : 'Sending...') 
                    : (language === 'es' ? 'Finalizar' : 'Finish')}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Componente para Guía II/III - Factores de Riesgo
const Guia3EmployeeDialog = ({ open, onOpenChange, empresaId, userId, onComplete }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Debounce timer ref for autosave
  const saveTimerRef = React.useRef(null);
  const pendingRef = React.useRef(null);
  
  // SC-M8-01 FIX-D: Load saved progress on open
  useEffect(() => {
    if (open && empresaId) {
      loadQuestions();
      loadProgress();
    }
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [open, empresaId]);
  
  const loadProgress = async () => {
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/guide3/progress`);
      if (res.data?.success && res.data.data?.answers) {
        const saved = res.data.data.answers;
        const parsed = {};
        for (const [k, v] of Object.entries(saved)) {
          parsed[parseInt(k)] = v;
        }
        if (Object.keys(parsed).length > 0) {
          setAnswers(parsed);
          setCurrentDomainIndex(res.data.data.last_domain_index || 0);
          setStarted(true); // Auto-start if resuming
        }
      }
    } catch (e) { /* silent — no progress yet */ }
  };
  
  const loadQuestions = async () => {
    setLoading(true);
    try {
      setQuestionnaireMode(true);
      
      const response = await axios.get(`/v5/nom035/${empresaId}/guide3/questions?lang=${language}`);
      if (response.data.success) {
        setQuestionnaire(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading guide3 questions:', error);
      toast.error(language === 'es' ? 'Error al cargar el cuestionario' : 'Error loading questionnaire');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAnswer = (itemId, value) => {
    setAnswers(prev => ({ ...prev, [itemId]: value }));
    // Debounced autosave (300ms) — cancels previous pending save for rapid clicking
    if (empresaId) {
      pendingRef.current = { item_id: itemId, valor: value };
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const payload = pendingRef.current;
        if (!payload) return;
        setSaving(true);
        axios.put(`/v5/nom035/${empresaId}/guide3/answer`, payload)
          .catch(() => {})
          .finally(() => setSaving(false));
      }, 300);
    }
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`/v5/nom035/${empresaId}/guide3/submit?user_id=${userId}`, {
        respuestas: answers
      });
      
      setCompleted(true);
      toast.success(language === 'es' ? 'Cuestionario completado' : 'Questionnaire completed');
      
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error) {
      logger.error('Error submitting guide3:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
    } finally {
      setSubmitting(false);
    }
  };
  
  const currentDomain = questionnaire?.dominios?.[currentDomainIndex];
  const totalDomains = questionnaire?.dominios?.length || 0;
  const totalItems = questionnaire?.total_items || 0;
  const answeredCount = Object.keys(answers).length;
  const progress = totalItems > 0 ? Math.round((answeredCount / totalItems) * 100) : 0;
  
  // Verificar si todas las preguntas del dominio actual están respondidas
  const currentDomainComplete = currentDomain?.items?.every(item => answers[item.id] !== undefined) || false;
  const allAnswered = answeredCount === totalItems;
  
  return (
    <Dialog open={open} onOpenChange={(val) => {
      if (!val) { setQuestionnaireMode(false); }
      onOpenChange(val);
    }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-teal-600" />
            {language === 'es' ? 'Guía III - Factores de Riesgo' : 'Guide III - Risk Factors'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es' 
              ? 'Evaluación de factores de riesgo psicosocial en tu entorno de trabajo.'
              : 'Evaluation of psychosocial risk factors in your work environment.'}
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-sm text-gray-500">{language === 'es' ? 'Cargando cuestionario...' : 'Loading questionnaire...'}</p>
          </div>
        ) : completed ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-semibold text-green-700">
              {language === 'es' ? '¡Gracias por completar el cuestionario!' : 'Thank you for completing the questionnaire!'}
            </h3>
          </div>
        ) : !started ? (
          <div className="py-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
            <p className="text-sm text-gray-600 mb-2">
              {language === 'es' 
                ? `Este cuestionario tiene ${totalItems} preguntas organizadas en ${totalDomains} secciones.`
                : `This questionnaire has ${totalItems} questions organized in ${totalDomains} sections.`}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {language === 'es' 
                ? 'Toma aproximadamente 20-30 minutos. Puedes guardarlo y continuar después.'
                : 'It takes approximately 20-30 minutes. You can save and continue later.'}
            </p>
            <Button onClick={() => setStarted(true)} className="bg-teal-600 hover:bg-teal-700">
              {language === 'es' ? 'Comenzar cuestionario' : 'Start questionnaire'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Progress bar */}
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-gray-500">
                <span>{language === 'es' ? 'Progreso' : 'Progress'}: {answeredCount}/{totalItems}</span>
                <div className="flex items-center gap-2">
                  {saving && <span className="text-teal-600 animate-pulse">{language === 'es' ? 'Guardando...' : 'Saving...'}</span>}
                  <span>{progress}%</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-teal-600 h-2 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
            
            {/* Domain tabs */}
            <div className="flex gap-1 overflow-x-auto pb-2">
              {questionnaire?.dominios?.map((domain, idx) => {
                const domainComplete = domain.items.every(item => answers[item.id] !== undefined);
                return (
                  <button
                    key={domain.id}
                    onClick={() => setCurrentDomainIndex(idx)}
                    className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                      idx === currentDomainIndex 
                        ? 'bg-teal-600 text-white' 
                        : domainComplete 
                          ? 'bg-green-100 text-green-700 border border-green-300'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {idx + 1}. {domain.nombre?.substring(0, 20)}...
                  </button>
                );
              })}
            </div>
            
            {/* Current domain questions */}
            {currentDomain && (
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-teal-700 border-b pb-2">
                  {currentDomain.nombre}
                </h4>
                
                {currentDomain.items.map((item, idx) => (
                  <div key={item.id} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm mb-3">{idx + 1}. {item.texto}</p>
                    <div className="flex flex-wrap gap-2">
                      {questionnaire.escala.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => handleAnswer(item.id, opt.value)}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                            answers[item.id] === opt.value
                              ? 'bg-teal-600 text-white border-teal-600'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'
                          }`}
                        >
                          {language === 'es' ? opt.label_es : opt.label_en}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Navigation buttons */}
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentDomainIndex(prev => Math.max(0, prev - 1))}
                disabled={currentDomainIndex === 0}
              >
                {language === 'es' ? 'Anterior' : 'Previous'}
              </Button>
              
              {currentDomainIndex < totalDomains - 1 ? (
                <Button
                  onClick={() => setCurrentDomainIndex(prev => prev + 1)}
                  disabled={!currentDomainComplete}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {language === 'es' ? 'Siguiente sección' : 'Next section'}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!allAnswered || submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {submitting 
                    ? (language === 'es' ? 'Enviando...' : 'Sending...') 
                    : (language === 'es' ? 'Finalizar cuestionario' : 'Finish questionnaire')}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NOM035EmployeePanel;
